import {
    Background,
    BackgroundVariant,
    Controls,
    Handle,
    MiniMap,
    Position,
    ReactFlow,
    getNodesBounds,
    getViewportForBounds,
    useEdgesState,
    useNodesState,
    type Connection,
    type NodeProps,
    type NodeTypes,
    type ReactFlowInstance,
    type Viewport
} from '@xyflow/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import {
    type SquirrelDiagramDesignerMountOptions,
    type SquirrelDiagramEdge,
    type SquirrelDiagramNode,
    type SquirrelDiagramNodeData,
    type SquirrelDiagramSnapshot
} from './types';

type DiagramDesignerAppProps = {
    options: SquirrelDiagramDesignerMountOptions;
    onSnapshotChanged: (snapshot: SquirrelDiagramSnapshot) => void;
    onExporterChanged: (exporter: (() => Promise<string>) | null) => void;
};

type PaletteItem = {
    label: string;
    nodeKind: string;
    badges: string[];
    pciRole?: string;
    scopeStatus?: string;
};

const exportImageWidth = 2200;
const exportImageHeight = 1320;
const exportImagePadding = 0.12;

async function waitForBrowserPaint(): Promise<void> {
    if ('fonts' in document) {
        await document.fonts.ready;
    }

    await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => resolve());
        });
    });
}

const paletteItems: PaletteItem[] = [
    {
        label: 'Website / App',
        nodeKind: 'website',
        badges: ['Website'],
        pciRole: 'Payment entry point'
    },
    {
        label: 'Vendor / Processor',
        nodeKind: 'vendor',
        badges: ['Vendor'],
        pciRole: 'Third party'
    },
    {
        label: 'Asset / Device',
        nodeKind: 'asset',
        badges: ['Asset'],
        pciRole: 'System involved'
    },
    {
        label: 'Employee / Role',
        nodeKind: 'employeeRole',
        badges: ['Employee'],
        pciRole: 'People/process'
    },
    {
        label: 'CDE Boundary',
        nodeKind: 'scopeBoundary',
        badges: ['Boundary'],
        scopeStatus: 'CDE'
    },
    {
        label: 'Note',
        nodeKind: 'note',
        badges: ['Note']
    }
];

const defaultNodes: SquirrelDiagramNode[] = [
    {
        id: 'customer',
        type: 'squirrel',
        position: { x: 0, y: 120 },
        data: {
            label: 'Customer',
            nodeKind: 'customer',
            pciRole: 'Cardholder / payer',
            badges: ['Customer']
        }
    },
    {
        id: 'checkout',
        type: 'squirrel',
        position: { x: 300, y: 120 },
        data: {
            label: 'Checkout / Payment Entry',
            nodeKind: 'checkout',
            pciRole: 'Payment entry point',
            scopeStatus: 'InScope',
            badges: ['Payment entry', 'In scope']
        }
    },
    {
        id: 'processor',
        type: 'squirrel',
        position: { x: 640, y: 120 },
        data: {
            label: 'Payment Processor',
            nodeKind: 'paymentProcessor',
            pciRole: 'Processor / acquirer path',
            badges: ['Processor']
        }
    }
];

const defaultEdges: SquirrelDiagramEdge[] = [
    {
        id: 'customer-to-checkout',
        source: 'customer',
        target: 'checkout',
        label: 'enters payment data',
        type: 'smoothstep',
        data: {
            label: 'enters payment data',
            dataType: 'AccountData'
        }
    },
    {
        id: 'checkout-to-processor',
        source: 'checkout',
        target: 'processor',
        label: 'submits securely',
        type: 'smoothstep',
        data: {
            label: 'submits securely',
            dataType: 'AccountData',
            encrypted: true
        }
    }
];

const defaultViewport: Viewport = {
    x: 100,
    y: 80,
    zoom: 1
};

const nodeTypes: NodeTypes = {
    squirrel: SquirrelNode
};



function SquirrelNode({ data, selected }: NodeProps<SquirrelDiagramNode>) {
    const nodeKind = asString(data.nodeKind, 'note');
    const label = asString(data.label, 'Diagram node');
    const icon = getNodeIcon(nodeKind);
    const toneClass = getNodeToneClass(nodeKind, asString(data.scopeStatus, ''));
    const badges = normalizeBadges(data);

    return (
        <div className={`sqd-node ${toneClass} ${selected ? 'selected' : ''}`}>
            <Handle type="target" position={Position.Left} className="sqd-node-handle" />

            <div className="sqd-node-header">
                <div className="sqd-node-icon" aria-hidden="true">
                    {icon}
                </div>

                <div className="sqd-node-title-wrap">
                    <div className="sqd-node-kicker">{getNodeKindLabel(nodeKind)}</div>
                    <div className="sqd-node-title">{label}</div>
                </div>
            </div>

            {(data.pciRole || data.scopeStatus || data.notes) && (
                <div className="sqd-node-body">
                    {data.pciRole && <div>{asString(data.pciRole, '')}</div>}
                    {data.scopeStatus && <div className="sqd-node-scope">{asString(data.scopeStatus, '')}</div>}
                    {data.notes && <div className="sqd-node-notes">{asString(data.notes, '')}</div>}
                </div>
            )}

            {badges.length > 0 && (
                <div className="sqd-node-badges">
                    {badges.map((badge) => (
                        <span key={badge}>{badge}</span>
                    ))}
                </div>
            )}

            <Handle type="source" position={Position.Right} className="sqd-node-handle" />
        </div>
    );
}

function normalizeBadges(data: SquirrelDiagramNodeData): string[] {
    if (!Array.isArray(data.badges)) {
        return [];
    }

    return data.badges
        .map((badge) => `${badge}`.trim())
        .filter((badge) => badge.length > 0)
        .slice(0, 4);
}

function asString(value: unknown, fallback: string): string {
    if (typeof value !== 'string') {
        return fallback;
    }

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : fallback;
}

function getNodeIcon(nodeKind: string): string {
    switch (nodeKind) {
        case 'customer':
            return '👤';
        case 'checkout':
            return '💳';
        case 'website':
            return '🌐';
        case 'vendor':
        case 'paymentProcessor':
            return '🏦';
        case 'asset':
            return '🖥';
        case 'employeeRole':
            return '👥';
        case 'scopeBoundary':
            return '▣';
        case 'dataStore':
            return '🗄';
        case 'networkSegment':
            return '🔌';
        case 'note':
            return '✎';
        default:
            return '●';
    }
}

function getNodeKindLabel(nodeKind: string): string {
    switch (nodeKind) {
        case 'customer':
            return 'Customer';
        case 'checkout':
            return 'Payment entry';
        case 'website':
            return 'Website';
        case 'vendor':
            return 'Vendor';
        case 'paymentProcessor':
            return 'Processor';
        case 'asset':
            return 'Asset';
        case 'employeeRole':
            return 'Role';
        case 'scopeBoundary':
            return 'Boundary';
        case 'dataStore':
            return 'Data store';
        case 'networkSegment':
            return 'Network';
        case 'note':
            return 'Note';
        default:
            return 'Node';
    }
}

function getNodeToneClass(nodeKind: string, scopeStatus: string): string {
    if (nodeKind === 'scopeBoundary') {
        return 'boundary';
    }

    if (nodeKind === 'note') {
        return 'note';
    }

    if (scopeStatus.toLowerCase().includes('out')) {
        return 'out-of-scope';
    }

    if (scopeStatus.toLowerCase().includes('cde')) {
        return 'cde';
    }

    if (scopeStatus.toLowerCase().includes('scope')) {
        return 'in-scope';
    }

    switch (nodeKind) {
        case 'customer':
            return 'customer';
        case 'checkout':
        case 'website':
            return 'entry';
        case 'vendor':
        case 'paymentProcessor':
            return 'vendor';
        case 'asset':
            return 'asset';
        case 'employeeRole':
            return 'people';
        default:
            return 'default';
    }
}

function isNodeArray(value: unknown): value is SquirrelDiagramNode[] {
    return Array.isArray(value);
}

function isEdgeArray(value: unknown): value is SquirrelDiagramEdge[] {
    return Array.isArray(value);
}

function isSelectedElement(value: { selected?: boolean }): boolean {
    return value.selected === true;
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
    return count === 1 ? singular : plural;
}

function normalizeNode(node: SquirrelDiagramNode): SquirrelDiagramNode {
    const data = node.data ?? { label: node.id };

    return {
        ...node,
        type: 'squirrel',
        selected: false,
        data: {
            ...data,
            label: asString(data.label, node.id)
        }
    };
}

function normalizeEdge(edge: SquirrelDiagramEdge): SquirrelDiagramEdge {
    const label =
        typeof edge.label === 'string' && edge.label.trim().length > 0
            ? edge.label
            : typeof edge.data?.label === 'string'
                ? edge.data.label
                : 'connects to';

    return {
        ...edge,
        type: edge.type || 'smoothstep',
        selected: false,
        label,
        data: {
            ...(edge.data ?? {}),
            label
        }
    };
}

function parseInitialDiagram(options: SquirrelDiagramDesignerMountOptions): {
    nodes: SquirrelDiagramNode[];
    edges: SquirrelDiagramEdge[];
    viewport: Viewport;
} {
    if (!options.diagramJson) {
        return {
            nodes: defaultNodes,
            edges: defaultEdges,
            viewport: defaultViewport
        };
    }

    try {
        const parsed = JSON.parse(options.diagramJson) as Partial<SquirrelDiagramSnapshot>;

        return {
            nodes: isNodeArray(parsed.nodes) && parsed.nodes.length > 0
                ? parsed.nodes.map(normalizeNode)
                : defaultNodes,
            edges: isEdgeArray(parsed.edges)
                ? parsed.edges.map(normalizeEdge)
                : defaultEdges,
            viewport: parsed.viewport ?? defaultViewport
        };
    } catch {
        return {
            nodes: defaultNodes,
            edges: defaultEdges,
            viewport: defaultViewport
        };
    }
}

function isEditableKeyboardTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    const tagName = target.tagName.toLowerCase();

    return tagName === 'input'
        || tagName === 'textarea'
        || tagName === 'select'
        || target.isContentEditable;
}

function getEditableBadges(value: unknown): string {
    if (!Array.isArray(value)) {
        return '';
    }

    return value
        .map((item) => `${item}`.trim())
        .filter((item) => item.length > 0)
        .join(', ');
}

export function DiagramDesignerApp({
    options,
    onSnapshotChanged,
    onExporterChanged
}: DiagramDesignerAppProps) {
    const initial = useMemo(() => parseInitialDiagram(options), [options.diagramJson]);

    const [nodes, setNodes, onNodesChange] = useNodesState<SquirrelDiagramNode>(initial.nodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState<SquirrelDiagramEdge>(initial.edges);
    const [viewport, setViewport] = useState<Viewport>(initial.viewport);
    const [nodeCounter, setNodeCounter] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const rootRef = useRef<HTMLDivElement | null>(null);

    const readOnly = options.readOnly === true;
    const diagramType = options.diagramType ?? 'PaymentFlow';

    const selectedNodeCount = nodes.filter(isSelectedElement).length;
    const selectedEdgeCount = edges.filter(isSelectedElement).length;
    const selectedCount = selectedNodeCount + selectedEdgeCount;

    const selectionSummary = selectedCount > 0
        ? `${selectedNodeCount} ${pluralize(selectedNodeCount, 'node')} · ${selectedEdgeCount} ${pluralize(selectedEdgeCount, 'edge')}`
        : 'No selection';

    const deleteSelectionLabel = selectedCount > 0
        ? `Delete selected (${selectedCount})`
        : 'Delete selected';

    const selectedNode = selectedNodeCount === 1
        ? nodes.find((node) => node.selected === true) ?? null
        : null;

    const selectedEdge = selectedNodeCount === 0 && selectedEdgeCount === 1
        ? edges.find((edge) => edge.selected === true) ?? null
        : null;

    const [reactFlowInstance, setReactFlowInstance] =
        useState<ReactFlowInstance<SquirrelDiagramNode, SquirrelDiagramEdge> | null>(null);

    const exportPng = useCallback(async (): Promise<string> => {
        if (!reactFlowInstance || !rootRef.current) {
            throw new Error('The diagram renderer is not ready yet.');
        }

        await waitForBrowserPaint();

        const renderedNodes = reactFlowInstance.getNodes();

        if (renderedNodes.length === 0) {
            throw new Error('The diagram does not contain any nodes.');
        }

        const viewportElement =
            rootRef.current.querySelector<HTMLElement>(
                '.react-flow__viewport');

        if (!viewportElement) {
            throw new Error('The diagram viewport could not be found.');
        }

        const bounds = getNodesBounds(renderedNodes);
        const { x, y, zoom } = getViewportForBounds(
            bounds,
            exportImageWidth,
            exportImageHeight,
            0.1,
            2,
            exportImagePadding);

        return await toPng(
            viewportElement,
            {
                backgroundColor: '#ffffff',
                cacheBust: true,
                width: exportImageWidth,
                height: exportImageHeight,
                pixelRatio: 1,
                style: {
                    width: `${exportImageWidth}px`,
                    height: `${exportImageHeight}px`,
                    transform: `translate(${x}px, ${y}px) scale(${zoom})`
                },
                filter: (node) => {
                    return !(
                        node instanceof HTMLElement &&
                        node.classList.contains('react-flow__handle')
                    );
                }
            });
    }, [reactFlowInstance]);

    useEffect(() => {
        if (!reactFlowInstance) {
            onExporterChanged(null);
            return;
        }

        onExporterChanged(exportPng);

        return () => {
            onExporterChanged(null);
        };
    }, [exportPng, onExporterChanged, reactFlowInstance]);

    useEffect(() => {
        setNodes(initial.nodes);
        setEdges(initial.edges);
        setViewport(initial.viewport);
    }, [initial, setEdges, setNodes]);

    useEffect(() => {
        onSnapshotChanged({
            schemaVersion: 1,
            engine: 'react-flow',
            diagramType,
            viewport,
            nodes,
            edges,
            updatedAtUtc: new Date().toISOString()
        });
    }, [diagramType, edges, nodes, onSnapshotChanged, viewport]);

    useEffect(() => {
        document.body.classList.toggle('sqd-fullscreen-active', isFullscreen);

        const resizeTimeout = window.setTimeout(() => {
            window.dispatchEvent(new Event('resize'));

            reactFlowInstance?.fitView({
                padding: isFullscreen ? 0.18 : 0.24,
                maxZoom: isFullscreen ? 1.15 : 1.1,
                duration: 320
            });
        }, 80);

        return () => {
            window.clearTimeout(resizeTimeout);
            document.body.classList.remove('sqd-fullscreen-active');
        };
    }, [isFullscreen, reactFlowInstance]);

    useEffect(() => {
        if (!isFullscreen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsFullscreen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isFullscreen]);

    const onConnect = useCallback(
        (connection: Connection) => {
            if (readOnly || !connection.source || !connection.target) {
                return;
            }

            const edgeLabel = 'connects to';

            const edge: SquirrelDiagramEdge = {
                id: `edge-${connection.source}-${connection.target}-${Date.now()}`,
                source: connection.source,
                target: connection.target,
                sourceHandle: connection.sourceHandle ?? undefined,
                targetHandle: connection.targetHandle ?? undefined,
                type: 'smoothstep',
                label: edgeLabel,
                data: {
                    label: edgeLabel
                }
            };

            setEdges((existingEdges: SquirrelDiagramEdge[]) => [...existingEdges, edge]);
        },
        [readOnly, setEdges]
    );

    const addNode = useCallback(
        (item: PaletteItem) => {
            if (readOnly) {
                return;
            }

            const nextCounter = nodeCounter + 1;
            const id = `manual-${item.nodeKind}-${Date.now()}-${nextCounter}`;

            setNodeCounter(nextCounter);

            const node: SquirrelDiagramNode = {
                id,
                type: 'squirrel',
                position: {
                    x: 160 + nextCounter * 36,
                    y: 260 + nextCounter * 24
                },
                data: {
                    label: item.label,
                    nodeKind: item.nodeKind,
                    pciRole: item.pciRole,
                    scopeStatus: item.scopeStatus,
                    badges: item.badges
                }
            };

            setNodes((existingNodes: SquirrelDiagramNode[]) => [...existingNodes, node]);
        },
        [nodeCounter, readOnly, setNodes]
    );

    const deleteSelected = useCallback(() => {
        if (readOnly) {
            return;
        }

        const selectedNodeIds = new Set(
            nodes
                .filter(isSelectedElement)
                .map((node) => node.id)
        );

        const selectedEdgeIds = new Set(
            edges
                .filter(isSelectedElement)
                .map((edge) => edge.id)
        );

        if (selectedNodeIds.size === 0 && selectedEdgeIds.size === 0) {
            return;
        }

        setNodes((existingNodes: SquirrelDiagramNode[]) =>
            existingNodes.filter((node) => !selectedNodeIds.has(node.id))
        );

        setEdges((existingEdges: SquirrelDiagramEdge[]) =>
            existingEdges.filter((edge) =>
                !selectedEdgeIds.has(edge.id)
                && !selectedNodeIds.has(edge.source)
                && !selectedNodeIds.has(edge.target))
        );
    }, [edges, nodes, readOnly, setEdges, setNodes]);

    const confirmDeleteSelected = useCallback(() => {
        if (readOnly || selectedCount === 0) {
            return;
        }

        const selectedNodeIds = nodes
            .filter(isSelectedElement)
            .map((node) => node.id);

        const selectedEdgeIds = edges
            .filter(isSelectedElement)
            .map((edge) => edge.id);

        const connectedEdgeCount = edges.filter((edge) =>
            selectedNodeIds.includes(edge.source) || selectedNodeIds.includes(edge.target)
        ).length;

        const parts: string[] = [];

        if (selectedNodeIds.length > 0) {
            parts.push(`${selectedNodeIds.length} ${pluralize(selectedNodeIds.length, 'node')}`);
        }

        if (selectedEdgeIds.length > 0) {
            parts.push(`${selectedEdgeIds.length} selected ${pluralize(selectedEdgeIds.length, 'edge')}`);
        }

        const connectedEdgeText = connectedEdgeCount > 0
            ? `\n\nThis will also remove ${connectedEdgeCount} connected ${pluralize(connectedEdgeCount, 'edge')}.`
            : '';

        const confirmed = window.confirm(
            `Delete ${parts.join(' and ')} from this diagram?${connectedEdgeText}\n\nThis change is not permanent until you save the diagram.`
        );

        if (!confirmed) {
            return;
        }

        deleteSelected();
    }, [deleteSelected, edges, nodes, readOnly, selectedCount]);

    useEffect(() => {
        if (readOnly) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.defaultPrevented || selectedCount === 0) {
                return;
            }

            if (isEditableKeyboardTarget(event.target)) {
                return;
            }

            if (event.key !== 'Delete' && event.key !== 'Backspace') {
                return;
            }

            event.preventDefault();
            confirmDeleteSelected();
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [confirmDeleteSelected, readOnly, selectedCount]);

    const printDiagram = useCallback(() => {
        if (!reactFlowInstance || !rootRef.current) {
            return;
        }

        const shouldExitFullscreenAfterPrint = !isFullscreen;

        const removeExistingPrintPortal = () => {
            document.getElementById('sqd-print-portal')?.remove();
        };

        const cleanup = () => {
            removeExistingPrintPortal();
            document.body.classList.remove('sqd-print-active');
            window.removeEventListener('afterprint', cleanup);

            if (shouldExitFullscreenAfterPrint) {
                setIsFullscreen(false);
            }

            window.setTimeout(() => {
                window.dispatchEvent(new Event('resize'));

                reactFlowInstance.fitView({
                    padding: 0.24,
                    maxZoom: 1.1,
                    duration: 0
                });
            }, 0);
        };

        removeExistingPrintPortal();
        document.body.classList.add('sqd-print-active');

        if (shouldExitFullscreenAfterPrint) {
            setIsFullscreen(true);
        }

        window.addEventListener('afterprint', cleanup);

        window.setTimeout(() => {
            window.dispatchEvent(new Event('resize'));

            reactFlowInstance.fitView({
                padding: 0.08,
                maxZoom: 0.7,
                duration: 0
            });

            window.setTimeout(() => {
                if (!rootRef.current) {
                    cleanup();
                    return;
                }

                const sourceRect = rootRef.current.getBoundingClientRect();

                const printableWidthPx = 10.3 * 96;
                const printableHeightPx = 7.45 * 96;

                const sourceWidth = Math.max(sourceRect.width, 1);
                const sourceHeight = Math.max(sourceRect.height, 1);

                const scale = Math.min(
                    0.7,
                    printableWidthPx / sourceWidth,
                    printableHeightPx / sourceHeight
                );

                const printPortal = document.createElement('div');
                printPortal.id = 'sqd-print-portal';

                const printPage = document.createElement('div');
                printPage.className = 'sqd-print-page';

                const printFrame = document.createElement('div');
                printFrame.className = 'sqd-print-frame';
                printFrame.style.setProperty('--sqd-print-source-width', `${sourceWidth}px`);
                printFrame.style.setProperty('--sqd-print-source-height', `${sourceHeight}px`);
                printFrame.style.setProperty('--sqd-print-scale', `${scale}`);

                const clonedDesigner = rootRef.current.cloneNode(true) as HTMLElement;
                clonedDesigner.classList.add('print-clone');

                printFrame.appendChild(clonedDesigner);
                printPage.appendChild(printFrame);
                printPortal.appendChild(printPage);
                document.body.appendChild(printPortal);

                window.print();
            }, 220);
        }, 320);
    }, [isFullscreen, reactFlowInstance]);

    const updateSelectedNodeData = useCallback(
        (key: keyof SquirrelDiagramNodeData, value: string) => {
            if (readOnly || !selectedNode) {
                return;
            }

            setNodes((existingNodes: SquirrelDiagramNode[]) =>
                existingNodes.map((node) => {
                    if (node.id !== selectedNode.id) {
                        return node;
                    }

                    const nextData = {
                        ...node.data,
                        [key]: value
                    };

                    return {
                        ...node,
                        data: nextData
                    };
                })
            );
        },
        [readOnly, selectedNode, setNodes]
    );

    const updateSelectedNodeBadges = useCallback(
        (value: string) => {
            if (readOnly || !selectedNode) {
                return;
            }

            const badges = value
                .split(',')
                .map((badge) => badge.trim())
                .filter((badge) => badge.length > 0)
                .slice(0, 4);

            setNodes((existingNodes: SquirrelDiagramNode[]) =>
                existingNodes.map((node) => {
                    if (node.id !== selectedNode.id) {
                        return node;
                    }

                    return {
                        ...node,
                        data: {
                            ...node.data,
                            badges
                        }
                    };
                })
            );
        },
        [readOnly, selectedNode, setNodes]
    );

    const updateSelectedEdgeLabel = useCallback(
        (value: string) => {
            if (readOnly || !selectedEdge) {
                return;
            }

            setEdges((existingEdges: SquirrelDiagramEdge[]) =>
                existingEdges.map((edge) => {
                    if (edge.id !== selectedEdge.id) {
                        return edge;
                    }

                    return {
                        ...edge,
                        label: value,
                        data: {
                            ...(edge.data ?? {}),
                            label: value
                        }
                    };
                })
            );
        },
        [readOnly, selectedEdge, setEdges]
    );

    const handleMoveEnd = useCallback(
        (_event: MouseEvent | TouchEvent | null, nextViewport: Viewport) => {
            setViewport(nextViewport);
        },
        []
    );

    return (
        <div ref={rootRef} className={`sqd-root ${isFullscreen ? 'fullscreen' : ''}`}>
            <div className="sqd-toolbar">
                <div className="sqd-toolbar-title">
                    <div className="sqd-eyebrow">PCI diagram designer</div>
                    <div className="sqd-title">{options.title || 'Squirrel Diagram Designer'}</div>
                    <div className="sqd-toolbar-hint">
                        Select a node or edge to delete it. Press Esc to leave full screen.
                    </div>
                </div>

                <div className="sqd-toolbar-actions">
                    <div className="sqd-action-group sqd-action-group-utility" aria-label="Diagram actions">
                        <button
                            type="button"
                            className={`sqd-tool-button ${isFullscreen ? 'active' : ''}`}
                            onClick={() => setIsFullscreen((value) => !value)}
                            aria-pressed={isFullscreen}
                        >
                            <span aria-hidden="true">{isFullscreen ? '↙' : '⛶'}</span>
                            {isFullscreen ? 'Exit full screen' : 'Full screen'}
                        </button>

                        <button
                            type="button"
                            className="sqd-tool-button"
                            onClick={printDiagram}
                            disabled={!reactFlowInstance}
                            title="Print this diagram or save it as a PDF."
                        >
                            <span aria-hidden="true">🖨</span>
                            Print / PDF
                        </button>

                        <button
                            type="button"
                            className="sqd-tool-button danger"
                            onClick={confirmDeleteSelected}
                            disabled={readOnly || selectedCount === 0}
                            title="Delete the selected node or edge. Keyboard Delete/Backspace also works while the designer is focused."
                        >
                            <span aria-hidden="true">🗑</span>
                            {deleteSelectionLabel}
                        </button>
                    </div>

                    <div className="sqd-action-group sqd-action-group-palette" aria-label="Add diagram nodes">
                        {paletteItems.map((item) => (
                            <button
                                key={item.nodeKind}
                                type="button"
                                className="sqd-tool-button"
                                onClick={() => addNode(item)}
                                disabled={readOnly}
                            >
                                <span aria-hidden="true">{getNodeIcon(item.nodeKind)}</span>
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="sqd-print-header" aria-hidden="true">
                <div>
                    <div className="sqd-print-kicker">Squirrel PCI diagram</div>
                    <div className="sqd-print-title">{options.title || 'Squirrel Diagram Designer'}</div>
                </div>
                <div className="sqd-print-meta">
                    {diagramType}
                </div>
            </div>

            <div className="sqd-canvas">
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    onInit={(instance) => setReactFlowInstance(instance)}
                    onNodesChange={readOnly ? undefined : onNodesChange}
                    onEdgesChange={readOnly ? undefined : onEdgesChange}
                    onConnect={readOnly ? undefined : onConnect}
                    defaultViewport={initial.viewport}
                    onMoveEnd={handleMoveEnd}
                    nodesDraggable={!readOnly}
                    nodesConnectable={!readOnly}
                    elementsSelectable={!readOnly}
                    deleteKeyCode={null}
                    minZoom={0.1}
                    maxZoom={2}
                    fitView
                    fitViewOptions={{
                        padding: 0.24,
                        maxZoom: 1.1
                    }}
                >
                    <Background variant={BackgroundVariant.Dots} gap={18} size={1} />
                    <MiniMap pannable zoomable />
                    <Controls showInteractive={!readOnly} />
                </ReactFlow>

                {selectedNode && (
                    <aside className="sqd-properties-panel" aria-label="Selected node properties">
                        <div className="sqd-properties-header">
                            <div>
                                <div className="sqd-properties-eyebrow">Selected node</div>
                                <h3>{asString(selectedNode.data.label, 'Diagram node')}</h3>
                            </div>
                            <span>{getNodeKindLabel(asString(selectedNode.data.nodeKind, 'note'))}</span>
                        </div>

                        <label className="sqd-field">
                            <span>Label</span>
                            <input
                                value={asString(selectedNode.data.label, '')}
                                onChange={(event) => updateSelectedNodeData('label', event.target.value)}
                                disabled={readOnly}
                            />
                        </label>

                        <label className="sqd-field">
                            <span>PCI role</span>
                            <input
                                value={asString(selectedNode.data.pciRole, '')}
                                onChange={(event) => updateSelectedNodeData('pciRole', event.target.value)}
                                disabled={readOnly}
                                placeholder="Payment entry point, processor, support role..."
                            />
                        </label>

                        <label className="sqd-field">
                            <span>Scope status</span>
                            <input
                                value={asString(selectedNode.data.scopeStatus, '')}
                                onChange={(event) => updateSelectedNodeData('scopeStatus', event.target.value)}
                                disabled={readOnly}
                                placeholder="InScope, OutOfScope, CDE..."
                            />
                        </label>

                        <label className="sqd-field">
                            <span>Badges</span>
                            <input
                                value={getEditableBadges(selectedNode.data.badges)}
                                onChange={(event) => updateSelectedNodeBadges(event.target.value)}
                                disabled={readOnly}
                                placeholder="Note, CDE, Website"
                            />
                        </label>

                        <label className="sqd-field">
                            <span>Notes</span>
                            <textarea
                                value={asString(selectedNode.data.notes, '')}
                                onChange={(event) => updateSelectedNodeData('notes', event.target.value)}
                                disabled={readOnly}
                                rows={5}
                                placeholder="Enter note text, auditor explanation, assumptions, or evidence context..."
                            />
                        </label>
                    </aside>
                )}

                {selectedEdge && (
                    <aside className="sqd-properties-panel" aria-label="Selected edge properties">
                        <div className="sqd-properties-header">
                            <div>
                                <div className="sqd-properties-eyebrow">Selected connection</div>
                                <h3>Flow connection</h3>
                            </div>
                            <span>Edge</span>
                        </div>

                        <label className="sqd-field">
                            <span>Label</span>
                            <input
                                value={asString(selectedEdge.data?.label, asString(selectedEdge.label, 'connects to'))}
                                onChange={(event) => updateSelectedEdgeLabel(event.target.value)}
                                disabled={readOnly}
                                placeholder="submits securely, redirects to processor..."
                            />
                        </label>
                    </aside>
                )}

                <div className="sqd-canvas-status" aria-live="polite">
                    <span>{selectionSummary}</span>
                    <span>Drag nodes · connect handles · save from the Payment Flow panel</span>
                </div>
            </div>
        </div>
    );
}